import { useState } from 'react'
import { styled } from '@mui/material/styles'
import Modal from '@mui/material/Modal'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import EditIcon from '@mui/icons-material/Edit'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import { DateTimePicker } from '@mui/x-date-pickers-pro'
import {
  useParams,
  useSubmit,
  useSearchParams,
  useNavigate,
  useLoaderData,
  useRouteLoaderData,
  useLocation,
} from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Controller, useForm, SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { isSameDay } from 'date-fns/isSameDay'
import { toast } from 'react-toastify'

import { useCurrentUserQuery } from '../../../services/user'
import {
  formatHumanDateTime,
  formatHumanTime,
  withExceptionCapturing,
  useLocationDataOptions,
  useRoomsDataOptions,
  searchString,
  getOption,
} from '../../../utils'
import { LoaderData } from '../../../types'
import ContributorsAutocomplete from '../../root/components/ContributorsAutocomplete'
import { useAcceptedKeysPerSubEventType } from '../../admin/create-event/step-2/components/SubEvents.utils'
import { SubEventSchema } from '../Events.schema'
import type { eventLoader } from '../Events.loader'
import type { subEventLoader } from './View.loader'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: { xs: 0, sm: 6 },
  boxShadow: 24,
  padding: { xs: 1, sm: 3 },
  width: { xs: '100%', sm: '90%' },
  maxWidth: '70rem',
  height: { xs: '100%', sm: '80%' },
  overflow: 'auto',
}

const Title = styled('h2')(() => ({
  marginBottom: 0,
}))
const Form = styled('form')(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'space-between',
  flex: '1 0 auto',
}))

interface Props {
  loaderId: 'events' | 'view-event'
}

export default function ViewSubEvent({ loaderId }: Props) {
  const { t } = useTranslation('events')
  const navigate = useNavigate()
  const location = useLocation()
  const submit = useSubmit()
  const { eventId, subEventId } = useParams()
  const [searchParams] = useSearchParams()
  const sessionId = searchParams.get('sessionId')
  if (!eventId) {
    throw new Error('eventId is required')
  }

  const { data: currentUserData } = useCurrentUserQuery()
  const { subEvent, sessions } = useLoaderData() as LoaderData<ReturnType<typeof subEventLoader>>
  const { event, sessions: sessionsOptions } = useRouteLoaderData(loaderId) as LoaderData<
    ReturnType<typeof eventLoader>
  >
  const isStaff = !!currentUserData?.is_staff
  const readOnly = !isStaff
  const required = isStaff

  const locationsOptions = useLocationDataOptions()
  const roomsOptions = useRoomsDataOptions(event?.location, locationsOptions)
  const [selectedSessions, setSelectedSessions] = useState(sessions)

  const methods = useForm<SubEventSchema>({
    defaultValues: subEvent,
    resolver: zodResolver(SubEventSchema),
  })
  const { control, handleSubmit, watch, formState } = methods
  const isDirty = formState.isDirty || Object.keys(formState.dirtyFields).length > 0

  const subEventType = watch('sub_event_type')

  const acceptedKeys = useAcceptedKeysPerSubEventType(subEventType)

  const handleClose = () => {
    if (
      !isStaff ||
      !isDirty ||
      window.confirm(
        t('Vous avez des modifications non enregistrées, voulez-vous vraiment quitter la page ?')
      )
    ) {
      const parentUrl = location.pathname.replace(/\/subEvent\/\d+/, '')
      navigate(parentUrl)
    }
  }

  const onSubmit: SubmitHandler<SubEventSchema> = subEventetails => {
    try {
      const data = SubEventSchema.safeParse(subEventetails)

      if ('error' in data && !data.success) {
        toast.error(t("Le schéma de donnée n'est pas valide"))
        data.error.issues.forEach(issue => {
          toast.warn(`${issue.path.toString()}: ${issue.message}`, {
            autoClose: 60000,
          })
        })
        console.error(data.error)
      }
      if ('data' in data && data.success) {
        submit(data.data, {
          method: 'POST',
          encType: 'application/json',
          action: `${location.pathname}?sessionId=${sessionId}`,
        })
      }
    } catch (e) {
      console.error(e)
      toast.error(t("Le schéma de donnée n'est pas valide"))
      toast.warn(t('Erreur innatendue'))
    }
  }

  const name = watch('name')

  return (
    <Modal open onClose={handleClose}>
      <Stack sx={style}>
        <Form onSubmit={withExceptionCapturing(handleSubmit(onSubmit))}>
          <Stack>
            <Stack direction="row" justifyContent="space-between">
              <Title>{!isStaff ? name : t("Détails de l'intervention")}</Title>
              {isStaff && (
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    if (
                      !isDirty ||
                      window.confirm(
                        "Vous avez des modifications non enregistrées, voulez-vous continuer vers l'édtion avancée ?"
                      )
                    ) {
                      navigate(`/admin/events/${eventId}/sessions/subEvent/${subEventId}`)
                    }
                  }}
                >
                  {t('Ouvrir édtion avancée')}
                </Button>
              )}
            </Stack>
            <Divider />
          </Stack>
          <Stack gap="0.5rem" flex="1 0" margin="2rem 0">
            {isStaff && (
              <Controller
                control={control}
                name="name"
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t('Nom')}
                    required={required}
                    variant="filled"
                    fullWidth
                    InputProps={{ readOnly }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    sx={{
                      display:
                        !acceptedKeys.includes(field.name) || (!isStaff && !field.value)
                          ? 'none'
                          : undefined,
                    }}
                  />
                )}
              />
            )}
            <Controller
              control={control}
              name="description"
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={t('Description')}
                  variant="filled"
                  InputProps={{ readOnly }}
                  fullWidth
                  multiline
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  sx={{
                    display:
                      !acceptedKeys.includes(field.name) || (!isStaff && !field.value)
                        ? 'none'
                        : undefined,
                  }}
                />
              )}
            />
            <Controller
              control={control}
              name="sessions"
              render={({ field, fieldState }) => (
                <Autocomplete
                  onChange={(_, value) => {
                    const ids = value.map(option => option?.id).filter(el => el !== undefined)
                    field.onChange(ids)
                    setSelectedSessions(value)
                  }}
                  value={selectedSessions}
                  onBlur={field.onBlur}
                  options={sessionsOptions}
                  id="sessionid-autocomplete"
                  disablePortal
                  fullWidth
                  multiple
                  readOnly={readOnly}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  filterOptions={x => x}
                  getOptionLabel={option => {
                    if (option) {
                      const sameDay = isSameDay(option.start, option.end)
                      return `${formatHumanDateTime(option.start, true)}-${sameDay ? formatHumanTime(option.end) : formatHumanDateTime(option.end, true)} : ${option.name}`
                    }
                    return 'None'
                  }}
                  popupIcon={readOnly ? null : undefined}
                  renderInput={params => (
                    <TextField
                      {...params}
                      name={field.name}
                      label={t('Séance(s) rattachée(s)')}
                      variant="filled"
                      InputProps={{ ...params.InputProps, readOnly }}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                  sx={{
                    display:
                      !acceptedKeys.includes(field.name) || (!isStaff && !field.value)
                        ? 'none'
                        : undefined,
                  }}
                />
              )}
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
              <Controller
                control={control}
                name="start"
                render={({ field, fieldState }) => (
                  <DateTimePicker
                    label={t('Date de début')}
                    name={field.name}
                    maxDateTime={
                      selectedSessions[0]?.end ? new Date(selectedSessions[0].end) : undefined
                    }
                    minDateTime={
                      selectedSessions[0]?.start ? new Date(selectedSessions[0].start) : undefined
                    }
                    defaultValue={field.value ? new Date(field.value) : null}
                    onChange={date => {
                      field.onChange(date?.toISOString())
                    }}
                    readOnly={readOnly}
                    slotProps={{
                      textField: {
                        required,
                        variant: 'filled',
                        error: !!fieldState.error,
                        helperText: fieldState.error?.message,
                      },
                    }}
                    sx={{
                      flex: '1 0 auto',
                      display:
                        !acceptedKeys.includes(field.name) || (!isStaff && !field.value)
                          ? 'none'
                          : undefined,
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name="end"
                render={({ field, fieldState }) => (
                  <DateTimePicker
                    label={t('Date de fin')}
                    name={field.name}
                    maxDateTime={
                      selectedSessions[0]?.end ? new Date(selectedSessions[0].end) : undefined
                    }
                    minDateTime={
                      selectedSessions[0]?.start ? new Date(selectedSessions[0].start) : undefined
                    }
                    defaultValue={field.value ? new Date(field.value) : null}
                    onChange={date => {
                      field.onChange(date?.toISOString())
                    }}
                    readOnly={readOnly}
                    slotProps={{
                      textField: {
                        required,
                        variant: 'filled',
                        error: !!fieldState.error,
                        helperText: fieldState.error?.message,
                      },
                    }}
                    sx={{
                      flex: '1 0 auto',
                      display:
                        !acceptedKeys.includes(field.name) || (!isStaff && !field.value?.length)
                          ? 'none'
                          : undefined,
                    }}
                  />
                )}
              />
            </Stack>
            <Controller
              control={control}
              name={'location'}
              render={({ field, fieldState }) => (
                <Autocomplete
                  onChange={(_, value) => field.onChange(value?.value ?? null)}
                  onBlur={field.onBlur}
                  defaultValue={getOption(locationsOptions, null, field.value)}
                  options={locationsOptions}
                  id="location-autocomplete"
                  disablePortal
                  fullWidth
                  readOnly={readOnly}
                  popupIcon={readOnly ? null : undefined}
                  renderInput={params => (
                    <TextField
                      {...params}
                      InputProps={{ ...params.InputProps, readOnly }}
                      label={t('Lieu')}
                      variant="filled"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                  sx={{
                    display:
                      !acceptedKeys.includes(field.name) || (!isStaff && !field.value)
                        ? 'none'
                        : undefined,
                  }}
                />
              )}
            />
            <Controller
              control={control}
              name="rooms"
              render={({ field, fieldState }) => (
                <Autocomplete
                  onChange={(_, value) => {
                    const parsedValue = value.reduce((acc, option) => {
                      if (option) acc.push(option.value)
                      return acc
                    }, [] as number[])
                    field.onChange(parsedValue)
                  }}
                  onBlur={field.onBlur}
                  value={field.value
                    .map(id => getOption(roomsOptions, null, id))
                    .filter(el => !!el)}
                  options={roomsOptions}
                  groupBy={option => option.group}
                  // Filter options when the user types. It should look for the rooms and the locations (label and group field)
                  filterOptions={(options, state) =>
                    options.filter(
                      option =>
                        searchString(option.label, state.inputValue) ||
                        searchString(option.group, state.inputValue)
                    )
                  }
                  getOptionLabel={option =>
                    option.groupId !== event?.location
                      ? `[${option.group}] ${option.label}`
                      : option.label
                  }
                  id="rooms-autocomplete"
                  multiple
                  disablePortal
                  fullWidth
                  readOnly={readOnly}
                  popupIcon={readOnly ? null : undefined}
                  renderInput={params => (
                    <TextField
                      {...params}
                      InputProps={{ ...params.InputProps, readOnly }}
                      label={t('Salle')}
                      variant="filled"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                  sx={{
                    display:
                      !acceptedKeys.includes(field.name) || (!isStaff && !field.value?.length)
                        ? 'none'
                        : undefined,
                  }}
                />
              )}
            />
            <Controller
              control={control}
              name="contributors"
              render={({ field, fieldState }) => (
                <ContributorsAutocomplete
                  prependName={field.name}
                  defaultValue={field.value}
                  onChange={field.onChange}
                  label={t('Contributeurs')}
                  variant="filled"
                  readOnly={readOnly}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  sx={{
                    display:
                      !acceptedKeys.includes(field.name) || (!isStaff && !field.value?.length)
                        ? 'none'
                        : undefined,
                  }}
                />
              )}
            />
          </Stack>
          <Stack
            direction="row"
            justifyContent={isStaff ? 'space-between' : 'flex-end'}
            paddingBottom="1.5rem"
            sx={{
              position: 'sticky',
              bottom: '-1rem',
              zIndex: 1,
              pointerEvents: 'none',
            }}
          >
            <Button
              onClick={e => {
                e.preventDefault()
                handleClose()
              }}
              sx={{ pointerEvents: 'all' }}
            >
              {t('Quitter')}
            </Button>
            {isStaff && (
              <LoadingButton variant="contained" type="submit" sx={{ pointerEvents: 'all' }}>
                {t('Modifier')}
              </LoadingButton>
            )}
          </Stack>
        </Form>
      </Stack>
    </Modal>
  )
}
